import React, {useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import {EyeIcon, FunnelIcon, CreditCardIcon} from "@heroicons/react/24/outline";
import StatusBadge from "../../../components/StatusBadge";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "react-query";
import {Link} from "react-router-dom";
import SortIcon from "../../../components/SortIcon";
import SelectInput from "../../../components/SelectInput";
import Loading from "../../../components/Loading";
import {apollo} from "../../../utils/apollo";
import {
    GET_MERCHANT_SUPPLIERS_LIST,
    GET_SETTLEMENT_LIST,
    GET_SETTLEMENT_REPORT_URL,
    GET_SETTLEMENT_RECEIPT_URL,
    REQUEST_SETTLEMENT
} from "../../../utils/graphql";
import IconButton from "../../../components/IconButton";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import PaginationFull from "../../../components/PaginationFull";
import Button from "../../../components/Button";
import {toast} from "react-toastify";
import {clearCachedKey} from "../../../utils/helpers";
import useAuthStore from "../../../store/auth";
import useModalStore from "../../../store/modal";

function Settlement() {
    const {t} = useTranslation();
    const {setModal} = useModalStore();
    const {merchant} = useAuthStore();
    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [supplier, setSupplier] = useState(null);
    const [page, setPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [statusFilter, setStatusFilter] = useState('')
    // const {data: records, isLoading, error} = useQuery('payment_links', getPaymentLinks)

    const tableHeader = [
        {column: 'settlement_uuid', title: t('settlement.table.settlement_uuid'), sortable: true},
        // {column: 'merchant_id', title: t('settlement.table.merchant_id'), sortable: false},
        // {column: 'user_id', title: t('settlement.table.user_id'), sortable: false},
        // {column: 'authority_id', title: t('settlement.table.authority_id'), sortable: false},
        ...(merchant?.allowSubVendors === true ? [{column: 'supplier', title: t('settlement.table.supplier'), sortable: false}] : []),
        {column: 'request_amount', title: t('settlement.table.request_amount'), sortable: true},
        {column: 'settlement_amount', title: t('settlement.table.settlement_amount'), sortable: true},
        {column: 'settlement_date', title: t('settlement.table.settlement_date'), sortable: true},
        {column: 'status', title: t('settlement.table.status'), sortable: true},
        {column: 'receipt', title: t('settlement.table.receipt'), sortable: false},
        {column: 'report', title: t('settlement.table.report'), sortable: false},
        {column: 'paymentReference', title: t('settlement.table.paymentReference'), sortable: false},
        // {column: 'created_at', title: t('settlement.table.created_at'), sortable: true},
        {column: 'actions', title: t('settlement.table.actions'), sortable: false, hidden: true},
    ]

    const requestSettlementMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: REQUEST_SETTLEMENT,
                variables: {
                    supplier: supplier,
                },
            });
            return data?.requestSettlement;
        },
        onSuccess: (data) => {
            if (data && data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message)
            }
            clearCachedKey(['settlement_list'])
        },
    })

    const getSettlementReportUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_REPORT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReportUrl) {
                window.location.href = data?.getSettlementReportUrl;
            }
        }
    })

    const getSettlementReceiptUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_RECEIPT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReceiptUrl) {
                window.location.href = data?.getSettlementReceiptUrl;
            }
        }
    })

    const fetchSettlements = useQuery(
        ['settlement_list', {page, sort, statusFilter}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SETTLEMENT_LIST,
                variables: {
                    sortBy: sort.field?.toUpperCase(),
                    sortDirection: sort.isAscending ? "ASC" : "DESC",
                    page,
                    first: 15,
                    filter: {
                        status: statusFilter
                    }
                },
            });
            return data?.getSettlementList;
        },
        {keepPreviousData: false}
    );
    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false,
        enabled: merchant?.allowSubVendors === true
    });

    const records = fetchSettlements?.data || null;
    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    const handleRequestSettlement = () => {
        if (requestSettlementMutation.isLoading) return;

        setModal({
            isVisible: true,
            title: t('settlement.confirm_settlement_title'),
            description: t('settlement.confirm_settlement_description'),
            confirmText: t('settlement.confirm_settlement_button'),
            onConfirm: () => {
                requestSettlementMutation.mutate();
            }
        });
    }

    return (
        <DashboardLayout>
            <div>
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c] shadow-sm border border-cyan-100/50">
                                <CreditCardIcon className="h-6 w-6" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{t('settlement.page_title')}</h2>
                        </div>
                        <p className="text-gray-500 text-sm">{t('settlement.page_description')}</p>
                    </div>
                    <Button
                        disabled={requestSettlementMutation.isLoading}
                        onClick={handleRequestSettlement}
                        className="rounded-full px-6 shadow-sm hover:shadow-md transition-all active:scale-95"
                    >
                        {t('settlement.request_settlement')}
                    </Button>
                </div>

                <div className="bg-gray-50/50 rounded-3xl p-1 mb-10 border border-gray-100">
                    <div className="bg-white rounded-[1.25rem] p-6 shadow-sm">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="p-1.5 rounded-lg bg-gray-100 text-gray-500">
                                <FunnelIcon className="h-4 w-4" />
                            </div>
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('settlement.filter_options')}</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {merchant?.allowSubVendors === true && (
                                <div className="space-y-1.5">
                                    <label htmlFor="supplier-filter" className="block text-xs font-semibold text-gray-500 uppercase ml-1">
                                        {t('settlement.filter.supplier')}
                                    </label>
                                    <SelectInput 
                                        name="supplier-filter" 
                                        id="supplier-filter" 
                                        get={supplier}
                                        set={v => setSupplier(v)}
                                        className="w-full !rounded-xl !border-gray-200 focus:!ring-blue-500/20"
                                    >
                                        <option value="">Merchant</option>
                                        {suppliersList?.map(supplier => <option key={supplier.supplierUuid} value={supplier.supplierUuid}>Vendor: {supplier.supplierName}</option>)}
                                    </SelectInput>
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <label htmlFor="status-filter" className="block text-xs font-semibold text-gray-500 uppercase ml-1">
                                    {t('settlement.filter.filter_by_status')}
                                </label>
                                <SelectInput 
                                    name="status-filter" 
                                    id="status-filter" 
                                    get={statusFilter}
                                    set={setStatusFilter}
                                    className="w-full !rounded-xl !border-gray-200 focus:!ring-blue-500/20"
                                >
                                    <option value="">{t('payment.filter.all')}</option>
                                    <option value="pending">{t('summary.table.pending_count')}</option>
                                    <option value="approved">{t('refund.approve')}</option>
                                    <option value="rejected">{t('refund.reject')}</option>
                                    <option value="under process">{t('pay.processing')}</option>
                                    <option value="completed">{t('summary.table.completed_count')}</option>
                                </SelectInput>
                            </div>
                        </div>
                    </div>
                </div>

                {fetchSettlements.isLoading && (
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-20 text-center">
                        <Loading message={t('settlement.loading_title') || 'Loading Settlements'} fullHeight={false} />
                    </div>
                )}

                {(fetchSettlements.isSuccess && (!records || !records?.data?.length)) && (
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-20 text-center">
                        <div className="mx-auto h-20 w-20 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 mb-6 border border-gray-100">
                            <CreditCardIcon className="h-10 w-10" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{t('settlement.no_data_title') || 'No Settlements Found'}</h3>
                        <p className="mt-2 text-gray-500 max-w-xs mx-auto mb-8">{t('settlement.no_data_description') || 'You have not created any settlements yet.'}</p>
                        <Button
                            onClick={() => fetchSettlements.refetch()} 
                            className="rounded-full px-8 shadow-sm"
                        >
                            {t('settlement.refresh_data') || 'Refresh Data'}
                        </Button>
                    </div>
                )}

                {(fetchSettlements.isSuccess && records && records?.data?.length > 0) && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between px-2 mb-2">
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{t('settlement.settlements_list') || 'Settlements List'}</h3>
                            <span className="text-xs font-bold text-[#0b6a8c] bg-cyan-50 px-3 py-1 rounded-full border border-cyan-100">
                                {records?.paginatorInfo?.total || records?.data?.length} {t('settlement.records') || 'records'}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-4">
                            {records?.data?.map((record) => (
                                <div key={record?.settlementUuid} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-cyan-100 transition-all group">
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                        <div className="flex items-start gap-4">
                                            <div className="hidden sm:flex h-12 w-12 rounded-xl bg-gray-50 items-center justify-center text-gray-400 group-hover:bg-cyan-50 group-hover:text-cyan-500 transition-colors">
                                                <CreditCardIcon className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-tight">{t('settlement.id')}</span>
                                                    <span className="text-sm font-semibold text-gray-900 font-mono">{record?.settlementUuid}</span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                    {merchant?.allowSubVendors === true && (
                                                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                            <span className="font-medium text-gray-400">{t('settlement.filter.supplier')}:</span>
                                                            <span className="font-semibold text-gray-700">{record?.supplier?.supplierName || 'N/A'}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                        <span className="font-medium text-gray-400">{t('settlement.date')}:</span>
                                                        <span className="font-semibold text-gray-700">{record?.settlementDate}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500 max-w-xs">
                                                        <span className="font-medium text-gray-400 whitespace-nowrap">{t('settlement.ref')}:</span>
                                                        <span className="font-semibold text-gray-700 truncate" title={record?.paymentReference}>{record?.paymentReference || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap items-center justify-between lg:justify-end gap-6 border-t lg:border-t-0 pt-4 lg:pt-0 border-gray-50">
                                            <div className="grid grid-cols-2 lg:flex lg:items-center gap-6">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{t('settlement.table.request_amount')}</span>
                                                    <span className="text-sm font-bold text-[#0b6a8c]">{record?.requestAmount} {record?.currency?.symbol}</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{t('settlement.table.settlement_amount')}</span>
                                                    <span className="text-sm font-bold text-green-600">{record?.settlementAmount} {record?.settlementAmount ? record?.currency?.symbol : 'N/A'}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <StatusBadge status={record?.status} className="!rounded-full px-3 py-1 text-[10px] w-28 justify-center" />
                                                
                                                <div className="flex items-center gap-1 border-l border-gray-100 pl-3">
                                                    {record?.receipt && (
                                                        <button 
                                                            onClick={() => {
                                                                if (record?.receipt?.startsWith('http')) {
                                                                    window.location.href = record.receipt;
                                                                } else {
                                                                    getSettlementReceiptUrlMutation.mutate({settlementUuid: record?.settlementUuid});
                                                                }
                                                            }}
                                                            disabled={getSettlementReceiptUrlMutation.isLoading}
                                                            className="p-2 text-gray-400 hover:text-[#0b6a8c] hover:bg-cyan-50 rounded-lg transition-all disabled:opacity-50"
                                                            title={t('settlement.download_receipt') || t('settlement.download')}
                                                        >
                                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <button 
                                                        onClick={() => getSettlementReportUrlMutation.mutate({settlementUuid: record?.settlementUuid})}
                                                        disabled={getSettlementReportUrlMutation.isLoading}
                                                        className="p-2 text-gray-400 hover:text-[#0b6a8c] hover:bg-cyan-50 rounded-lg transition-all disabled:opacity-50"
                                                        title={t('settlement.download')}
                                                    >
                                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </button>
                                                    <ButtonInternalLink
                                                        href={`/merchant/settlements/view/${record?.settlementUuid}`}
                                                        className="!p-2 !bg-transparent !text-gray-400 hover:!text-[#0b6a8c] hover:!bg-cyan-50 !rounded-lg !border-0 transition-all shadow-none"
                                                    >
                                                        <EyeIcon className="h-5 w-5" />
                                                    </ButtonInternalLink>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="bg-gray-50/50 rounded-2xl p-4 mt-8 border border-gray-100">
                            <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default Settlement;
